import { EventAggregator } from 'aurelia-event-aggregator';
import { autoinject, computedFrom, Container } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import { Router, RouterConfiguration } from 'aurelia-router';
import { StateHistory, Store } from 'aurelia-store';
import cloneDeep from 'lodash-es/cloneDeep';
import throttle from 'lodash-es/throttle';

import { LoginApi } from '~/api/login-api';
import { NavigationAPI } from '~/api/navigation-api';
import { QasApi } from '~/api/qas-api';
import { AppActions } from '~/app-actions';
import { AppRouteID, AppRoutes } from '~/app-routes';
import { AppState, STATE_LOCAL_STORAGE_KEY } from '~/app-store';
import { Authentication } from '~/authentication/authentication';
import { BaseViewModel } from '~/base-view-model';
import { EntitySearchActions } from '~/components/entity-search/entity-search-actions';
import { Navigation } from '~/components/navigation/navigation';
import { NavigationActions } from '~/components/navigation/navigation-actions';
import { NavigationState } from '~/components/navigation/navigation-state';
import { computeFirstRouteInStructure } from '~/components/navigation/navigation-utils';
import { Notification } from '~/components/notification/notification';
import {
	CommonURLParam,
	ShortcutKey,
	STORE_VERSION,
	TREE_VIEW_SESSION_VARIABLE_NAME,
} from '~/constants';
import { CustomisationEditorSelectDialogManager } from '~/forms/customisation-editor/customisation-editor-select-dialog-manager';
import { LoginActions } from '~/login/login-actions';
import { OktaLogin } from '~/login/okta-login';
import { AuthorizeStep } from '~/pipelines/authorize';
import { BrowserCheckStep } from '~/pipelines/browser-check';
import { DirtyCheckStep } from '~/pipelines/dirty-check-step';
import { InitialLoadStep } from '~/pipelines/initial-load-step';
import { LoggingStep } from '~/pipelines/logging';
import { UrlParameterStep } from '~/pipelines/url-parameter';
import { SettingsState } from '~/settings-state/settings-state';
import { DialogUtils } from '~/utils/dialog-utils';
import { KeyboardShortcut } from '~/utils/keyboard-shortcut';
import { clearLocalLoginData, setItem } from '~/utils/local-storage';
import { Logger } from '~/utils/logger';
import { ReplaceConfigLabelStep } from './pipelines/replace-config-label-step';
import { DirtyCheckAggregator } from './utils/dirty-check-aggregator';

export const AppEvents = {
	FocusOnEntity: 'App.FocusOnEntity',
	Login: 'App.Login',
	LoginStatusChanged: 'App.LoginStatusChanged',
	Logout: 'App.Logout',
	NavigationFetch: 'App.NavigationFetch',
	NavigationToggle: 'App.NavigationToggle',
	Reset: 'App.Reset',
	TokenRefreshed: 'App.TokenRefreshed',
};

@autoinject
export class App extends BaseViewModel {
	/**
	 * Event IDs
	 */
	public static readonly Events = AppEvents;
	/**
	 * One-time check for checking the state version
	 */
	private didCheckStateVersion: boolean = false;

	constructor(
		protected readonly store: Store<StateHistory<AppState>>,
		protected readonly i18n: I18N,
		protected readonly eventAggregator: EventAggregator,
		private readonly dialogUtils: DialogUtils,
		private readonly loginApi: LoginApi,
		private readonly qasService: QasApi,
		private readonly settingsState: SettingsState,
		private readonly customisationEditorSelectDialogManager: CustomisationEditorSelectDialogManager,
		private readonly navigationApi: NavigationAPI,
		private readonly oktaLogin: OktaLogin,
		private readonly authentication: Authentication,
		private readonly dirtyCheckAggregator: DirtyCheckAggregator,
	) {
		super();
		this.i18n.setLocale();

		// Register actions that we need right away
		this.store.registerAction(
			'App.LoadStateFromLocalStorage',
			AppActions.LoadStateFromLocalStorage,
		);
		this.store.registerAction('App.ResetState', AppActions.ResetState);
		this.store.registerAction('App.LoginUser', LoginActions.LoginUser);

		this.store.registerAction(
			'Navigation.AddEntityToHistory',
			EntitySearchActions.AddEntityToHistory,
		);

		this.store.registerAction(
			'Navigation.ToggleBookmark',
			NavigationActions.ToggleBookmark,
		);
		this.store.registerAction(
			'Navigation.CollapseNavigation',
			NavigationActions.CollapseNavigation,
		);
		this.store.registerAction(
			'Navigation.LogNavigationHistory',
			NavigationActions.LogNavigationHistory,
		);

		// Load state from localStorage right away
		this.dispatch(AppActions.LoadStateFromLocalStorage);
	}

	/**
	 * Initialize router configuration
	 */
	public async configureRouter(config: RouterConfiguration, router: Router) {
		this.router = router;

		config.title = this.i18n.tr('app.title');
		// NOTE(Albert): 2020-10-19 - WEBUI-3579
		// If we're in Tree View, skip this step as we do not want to diplay log in screen
		// on WEBUI. By the time we load the first WEBUI form, we will already be logged in
		if (sessionStorage.getItem(TREE_VIEW_SESSION_VARIABLE_NAME) !== '1') {
			config.addAuthorizeStep(AuthorizeStep);
		}
		config.addAuthorizeStep(InitialLoadStep);
		config.addPreActivateStep(ReplaceConfigLabelStep);
		config.addPreActivateStep(UrlParameterStep);
		config.addPreActivateStep(DirtyCheckStep);
		config.addPreRenderStep(BrowserCheckStep);
		config.addPreRenderStep(LoggingStep);
		config.map(AppRoutes);

		// NOTE: Jake: 2019-06-21 - WEBUI-2185
		// fallbackRoute is used when navigating to a page is rejected.
		// ie. activate() is rejected
		// Unfortunately we seemingly can't capture the rejection message in a clean
		// way.
		// A quirk with uncaught errors in the activate() method of pages is that if you
		// navigate from an error-404 page, it will stay on that error-404 page.
		config.fallbackRoute('error');

		config.mapUnknownRoutes(async (instruction) => {
			// Set redirect to the first route that's not the 'login' page
			let redirect = null;

			const path = instruction.params.path;
			// If attempting to go to a non-empty path, redirect to 404 error page
			if (path !== '/') {
				redirect = 'error-404';
			} else {
				redirect = await this.computeFirstRouteInNavigation(redirect);
			}

			return {
				route: ['.*'],
				redirect,
			};
		});
	}

	public activate(): Promise<any> {
		this.subscribe(); // Need to subscribe to events in activate, before they are used in any PipelineStep
		return Promise.resolve();
	}

	public attached() {
		// Initialize tooltips
		$('[data-toggle="tooltip"]').tooltip();

		if (sessionStorage.getItem(TREE_VIEW_SESSION_VARIABLE_NAME) !== '1') {
			// Customisation editor is not currently supported in tree-view
			KeyboardShortcut.subscribe(
				this,
				ShortcutKey.CustomisationEditor,
				() => this.checkAuthAndOpenCustomisationEditorSelectDialog(),
			);
		}

		this.initApplicationYMLStyling();
	}

	public getDirtyCheckAggregator() {
		return this.dirtyCheckAggregator;
	}

	/**
	 * initApplicationYMLStyling creates an inline <style> element with styling from the application.yml file.
	 * We do this instead of applying "style" directly so that we can also leverage :hover/:focus states.
	 */
	private initApplicationYMLStyling(): void {
		const { navBackgroundColor } = this.settingsState.state;
		// NOTE: Jake: 2019-10-23
		// Uncomment these for quick debugging of custom colors
		// navBackgroundColor = '#ff0000'; // red
		// navBackgroundColor = '#00ff00'; // green
		// navBackgroundColor = '#0000ff'; // blue
		// navBackgroundColor = '#ffffff'; // white
		// navBackgroundColor = '';        // force disable
		if (!navBackgroundColor) {
			return;
		}
		if (navBackgroundColor.length > 0 && navBackgroundColor[0] !== '#') {
			// NOTE: Jake: 2019-10-23
			// Only support 6-digit hex codes so we can calculate the inverse
			// easily.
			return;
		}
		if (navBackgroundColor.length > 7) {
			// NOTE: Jake: 2019-10-23 - WEBUI-2531
			// Disallow more than 7 characters to avoid people using hacks to insert custom
			// CSS. We also validate this config in the backend.
			throw new Error(
				'navBackgroundColor cannot have more than 7 characters.',
			);
		}

		// NOTE: Jake: 2019-10-23 - WEBUI-2531
		// We use the same shade here:
		// - devel\srcjava\AcurityWeb\src\main\javascript\src\components\navigation\_navigation-menu.scss
		const shade = 0.35;

		const inverseColor = App.hexInverseBw(navBackgroundColor);

		const labelBackgroundColor = App.shadeHexColor(
			navBackgroundColor,
			inverseColor === '#000000' ? -shade : shade,
		);
		const labelFontColor = App.hexInverseBw(labelBackgroundColor);

		// NOTE: Jake: 2019-10-23 - WEBUI-2531
		// When/if we improve navigation.ts to use CSS Modules, we should change these selectors
		// to be tied to the styles we import, so that if a selector is removed, we get a compile-time
		// error that leads us to this code.
		const css = `
			/**
			 * Generated at runtime based on api/settings/user
			 */
			.a-app__nav-label {
				color: ${labelFontColor};
				background-color: ${labelBackgroundColor};
			}

			.a-app__menu {
				background-color: ${navBackgroundColor};
				color: ${inverseColor};
			}

			.a-app__menu .expandable > ul {
				background-color: rgba(255, 255, 255, 0.15);
			}

			.a-app__menu .a-app__menu__filter__input,
			.a-app__menu .a-app__menu__filter__icon {
				color: ${inverseColor};
			}
			.a-app__menu .a-app__menu__filter__input::placeholder {
				color: ${inverseColor};
			}
			.a-app__menu .a-app__menu__filter__input::-ms-input-placeholder {
				color: ${inverseColor};
			}

			.a-app__menu .a-app__menu__filter__input:focus,
			.a-app__menu .a-app__menu__vertical__clickable.selected,
			.a-app__menu .a-app__menu__vertical__clickable:hover,
			.a-app__menu .a-app__menu__vertical__clickable:focus {
				background-color: ${inverseColor};
			}

			.a-app__menu .a-app__menu__filter__input:focus,
			.a-app__menu .a-app__menu__filter__input:focus + .a-app__menu__filter__icon,
			.a-app__menu .a-app__menu__vertical__clickable.selected,
			.a-app__menu .a-app__menu__vertical__clickable:hover,
			.a-app__menu .a-app__menu__vertical__clickable:focus {
				color: ${navBackgroundColor};
			}
			.a-app__menu .a-app__menu__filter__input:focus::placeholder {
				color: ${navBackgroundColor};
			}
			.a-app__menu .a-app__menu__filter__input:focus::-ms-input-placeholder {
				color: ${navBackgroundColor};
			}
		`;
		const style = document.createElement('style');
		style.type = 'text/css';
		style.appendChild(document.createTextNode(css));

		const head = document.head || document.getElementsByTagName('head')[0];
		head.appendChild(style);
	}

	/**
	 * shadeHexColor will lighten/darken a color depending on whether a positive or negative percent is given.
	 *
	 * @source https://github.com/PimpTrizkit/PJs/wiki/12.-Shade,-Blend-and-Convert-a-Web-Color-(pSBC.js)#stackoverflow-archive-begin
	 */
	private static shadeHexColor(color: string, percent: number) {
		const f = parseInt(color.slice(1), 16);
		const t = percent < 0 ? 0 : 255;
		const p = percent < 0 ? percent * -1 : percent;
		// eslint-disable-next-line no-bitwise
		const R = f >> 16;
		// eslint-disable-next-line no-bitwise
		const G = (f >> 8) & 0x00ff;
		// eslint-disable-next-line no-bitwise
		const B = f & 0x0000ff;
		return (
			'#' +
			(
				0x1000000 +
				(Math.round((t - R) * p) + R) * 0x10000 +
				(Math.round((t - G) * p) + G) * 0x100 +
				(Math.round((t - B) * p) + B)
			)
				.toString(16)
				.slice(1)
		);
	}

	private static hexInverseBw(hex: string): '#ffffff' | '#000000' {
		if (!hex) {
			throw new Error('Invalid parameter');
		}
		const rgbParsed = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
		if (!rgbParsed) {
			throw new Error('Unable to parse 6-digit hex color.');
		}
		const rgb = {
			r: parseInt(rgbParsed[1], 16),
			g: parseInt(rgbParsed[2], 16),
			b: parseInt(rgbParsed[3], 16),
		};
		const luminance = 0.2126 * rgb.r + 0.7152 * rgb.g + 0.0722 * rgb.b;
		return luminance < 140 ? '#ffffff' : '#000000';
	}

	private checkAuthAndOpenCustomisationEditorSelectDialog(): void {
		this.customisationEditorSelectDialogManager.checkAuthAndOpenCustomisationEditorSelectDialog(
			this.router,
			{},
		);
	}

	/**
	 * Subscribe to events
	 */
	private subscribe(): void {
		Logger.log('App is now subscribing to state changes');
		// Write local state to localStorage
		this.subscribeToStateChanges({
			callback: throttle(
				(stateHistory) => this.writeStateToLocalStorage(stateHistory),
				1000,
			),
			requireLoggedIn: true,
		});

		this.subscribeToEvent(App.Events.Logout, (errorMessage) =>
			this.logoutUser(errorMessage),
		);
	}

	/**
	 * Write selected state to localstorage
	 */
	private writeStateToLocalStorage(stateHistory) {
		const currentState = cloneDeep(stateHistory.present);

		if (!this.didCheckStateVersion && currentState) {
			this.verifyStateVersion(stateHistory);
		}

		setItem(STATE_LOCAL_STORAGE_KEY, JSON.stringify(stateHistory.present));
	}

	private async invalidateLogin(): Promise<void> {
		const accessToken: string =
			await this.authentication.getLocalOrOktaAccessToken();
		if (accessToken && accessToken.isNotBlank()) {
			return this.loginApi.logout(); // invalidate token on server
		}
		return Promise.resolve();
	}

	/**
	 * Set `global.isLoggedIn` state to `false`
	 * Then clear storage and redirect to login page
	 *
	 * @param errorMessage Message to display after the page is reloaded
	 */
	public logoutUser(errorMessage: string | undefined) {
		let logout: () => Promise<void>;
		if (OktaLogin.isLoggedIn()) {
			logout = () => this.oktaLogin.logout();
		} else {
			logout = () => this.invalidateLogin();
		}

		logout()
			// Waiting until we sent the invalidation request before clearing our cache
			.finally(() => {
				clearLocalLoginData();
				this.resetState().then(() => {
					Logger.log('Navigating to login');
					// only navigate to route login if it is not at login page yet
					if (!window.location.hash.startsWith('#/login')) {
						// Preserve `redirectFrom` param if given. If not, this'll just be empty
						const params = new URLSearchParams(
							window.location.hash.split('?')[1],
						);
						this.router.navigateToRoute(AppRouteID.Login, {
							errorMessage,
							redirectFrom: params.get(
								CommonURLParam.REDIRECT_FROM,
							),
						});
					}
				});
			});
	}

	/**
	 * Resets the state
	 */
	private resetState(): Promise<void> {
		Logger.log('Resetting the state ...');
		return this.dispatch(AppActions.ResetState);
	}

	/**
	 * Updates the state based on the state version
	 */
	private verifyStateVersion(stateHistory: StateHistory<AppState>) {
		const triggerNotif = (notifType: 'info' | 'error', message: string) => {
			// Wait for the page to render first before popping notification
			setTimeout(() => {
				Notification[notifType](message);
			});
			Logger.log(message);
		};
		try {
			const [appMajVer, appMinVer] = STORE_VERSION.split('.');
			const [stateMajVer, stateMinVer] =
				stateHistory.present.version.split('.');
			const changeStateMessage = `State version is updated: ${stateMajVer}.${stateMinVer} -> ${appMajVer}.${appMinVer}.`;

			if (appMajVer === stateMajVer) {
				if (appMinVer !== stateMinVer) {
					triggerNotif(
						'info',
						`${changeStateMessage} However, no state reset is needed.`,
					);
				} else {
					Logger.log('State version:', STORE_VERSION);
				}
			} else {
				triggerNotif(
					'info',
					`${changeStateMessage} Updating the state.`,
				);

				// Reset state, but keep the login state
				this.dispatch(
					AppActions.ResetState,
					{
						isLoggedIn: stateHistory.present.isLoggedIn,
						version: STORE_VERSION,
					},
					true,
				);
			}
			this.didCheckStateVersion = true;
		} catch (error) {
			const errorMessage =
				'There is something wrong with the state of the app. Resetting to default state...';
			triggerNotif('error', errorMessage);
			// Log the user out to avoid complications with the state
			this.logoutUser(errorMessage);
		}
	}

	@computedFrom('appState.present.navigation.collapsed')
	private get isNavigationCollapsed() {
		const navigation = this.appState.present.navigation;
		if (!navigation) {
			return Navigation.initialState.collapsed;
		}
		return navigation.collapsed;
	}

	/**
	 * Try to navigate to the first route in the navigational structure.
	 * This is preferred over routing to the first route in the route config.
	 */
	private async computeFirstRouteInNavigation(
		route: string,
	): Promise<string> {
		try {
			const navState = Container.instance.get(NavigationState);

			await navState.maybeFetchNavigation('');
			if (navState.viewNameList && navState.viewNameList.length >= 1) {
				const viewName = navState.viewNameList[0];
				await navState.maybeFetchNavigation(viewName);
				route = computeFirstRouteInStructure(
					navState.structure[viewName].structure.entityList[0],
				);
			}
		} catch (e) {
			Logger.warn(
				'Failed to compute default unknown route using navigational structure.',
				e,
			);
		}
		return route;
	}
}
